/**
 * PhoneBatchCompleteModal
 *
 * Shown once per session when a phone verification batch finishes — either
 * live while the user is logged in, or on next login if they were away. The
 * worker writes /clients/{id}.pendingPhoneBatchNotification when a job
 * completes and the dashboard listener triggers this modal.
 *
 * "Got It" calls dismissPhoneBatchNotification to clear the flag.
 */

import { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../services/firebase';

interface Props {
  runType: string;       // 'disconnect' | 'disconnect_plus_reverse'
  goodAdded: number;     // leads written to /leads after verification
  badRemoved: number;    // phones filtered as bad
  completedAt: string;
  onClose: () => void;
}

export function PhoneBatchCompleteModal({ runType, goodAdded, badRemoved, completedAt, onClose }: Props) {
  const [dismissing, setDismissing] = useState(false);

  const handleDismiss = async () => {
    setDismissing(true);
    try {
      const fn = httpsCallable(functions, 'dismissPhoneBatchNotification');
      await fn({});
    } catch (err) {
      console.error('Failed to dismiss batch complete notification:', err);
    } finally {
      onClose();
    }
  };

  const friendlyRunType = runType === 'disconnect_plus_reverse'
    ? 'disconnect + reverse phone'
    : 'phone disconnect';

  const totalProcessed = goodAdded + badRemoved;

  return (
    <div className="modal-overlay" style={overlayStyle}>
      <div className="modal-content" style={contentStyle}>
        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
          <div style={{ fontSize: '48px', marginBottom: '12px' }}>✅</div>
          <h2 style={{ margin: 0, color: 'var(--gold)' }}>Verification Complete</h2>
        </div>

        <p style={{ color: 'var(--gray-200)', lineHeight: 1.6, margin: '0 0 16px' }}>
          Your <strong>{friendlyRunType}</strong> verification finished
          {totalProcessed > 0 && <> processing <strong style={{ color: 'var(--gold)' }}>{totalProcessed.toLocaleString()}</strong> {totalProcessed === 1 ? 'phone' : 'phones'}</>}.
        </p>

        <div style={statsBoxStyle}>
          <div style={statRowStyle}>
            <span style={{ color: 'var(--gray-300)' }}>Verified leads added</span>
            <strong style={{ color: 'var(--green)' }}>{goodAdded.toLocaleString()}</strong>
          </div>
          <div style={statRowStyle}>
            <span style={{ color: 'var(--gray-300)' }}>Disconnected phones removed</span>
            <strong style={{ color: 'var(--gray-400)' }}>{badRemoved.toLocaleString()}</strong>
          </div>
        </div>

        <p style={{ color: 'var(--gray-400)', fontSize: '0.85rem', lineHeight: 1.6, margin: '16px 0 0' }}>
          The verified leads are ready to call from your dashboard.
          {completedAt && <> Completed {new Date(completedAt).toLocaleString()}.</>}
        </p>

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '24px' }}>
          <button
            onClick={handleDismiss}
            disabled={dismissing}
            className="btn btn-primary"
          >
            {dismissing ? 'Dismissing...' : 'Got It'}
          </button>
        </div>
      </div>
    </div>
  );
}

const overlayStyle: React.CSSProperties = {
  position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
  background: 'rgba(0, 0, 0, 0.7)', zIndex: 1000,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  padding: '16px',
};

const contentStyle: React.CSSProperties = {
  background: 'var(--bg-primary, #1e1e1e)', borderRadius: '12px',
  padding: '32px', maxWidth: '480px', width: '100%',
  border: '1px solid rgba(200, 168, 78, 0.3)',
  maxHeight: '90vh', overflowY: 'auto',
};

const statsBoxStyle: React.CSSProperties = {
  padding: '14px 18px', borderRadius: '8px',
  background: 'rgba(200, 168, 78, 0.08)',
  border: '1px solid rgba(200, 168, 78, 0.2)',
};

const statRowStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  padding: '6px 0',
};
